import { enableMapSet } from 'immer';
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import { DefaultVersionEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { ApiError } from '@api/apiRuntime';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import * as yup from 'yup';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import SvgCalendar from '@components/framework/icons/Calendar.tsx';
import SvgForExpedition from '@icons/ForExpedition.tsx';
import { ColWithGap, HorizontalLine } from '@components/styles.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgRepeatIcon from '@app/icons/admin/icon/RepeatIcon.tsx';
import {
  AttachmentUploadedOutDTO,
  CheckDuplicationExpeditionResponseOutDTO,
  DuplicationLocationOutDTO,
  DuplicationMoreReservationTimeslotsDTO, EntityTypeEnum,
  ExpeditionDetailV4OutDTO,
  FeaturesEnum,
  FreeSlotsWrapperOutDTO,
  OpeningHourStateEnum,
  OpeningModeEnum,
  PermissionTypesEnum,
  ReservationStateEnum,
  ResponseDeadlineModeEnum,
  TimeslotCalendarDTO,
} from '@api/ringil-api.v2.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { isNotNullOrUndefined, isNullOrUndefined, parseNumberOrUndefined } from '@utils/utils.tsx';
import COText from '@app/pages/COText/COText.tsx';
import clsx from 'clsx';
import { useDebounceWithEvents } from '@hooks/useDebounceWithEvents/useDebounceWithEvents.tsx';
import { remapOpeningHours } from '@components/ringil3/Features/Expedition/DuplicateExpeditionSidebar/common.ts';
import SvgIconFile from '@components/framework/icons/IconFile.tsx';
import { Link } from 'react-router-dom';
import useNavigation from '@hooks/useNavigation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import DuplicateDatesButtons from '@components/features/DuplicateDatesButtons/DuplicateDatesButtons.tsx';
import SvgWarningExclamation from '@components/ringil3/icons/WarningExclamation.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import SvgAlert from '@icons/alert-circle.svg?react';
import SvgOK from '@icons/check-circle.svg?react';
import SvgQuestionMark from '@icons/question-mark.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import CarrierDistributionSelector, {
  CarrierDistributionSelectorData,
  DistributionType,
  distributionTypesForShipment,
  getSelectedUserIds,
  resolveResponseDeadline,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { contractedCarriersValidation } from '@app/pages/expedition/create/validation.ts';
import { muCodesOf } from '@app/pages/expedition/create/contractedUnits.ts';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import Panel from '@components/ringil3/Panel/Panel.tsx';
import { ClickableCursor } from '@components/ringil3/styles.tsx';
import COSelect from '@components/ringil3/COSelect/COSelect.tsx';
import {
  apiUtcFromDayjs,
  dayjsFromApiUtc,
  dayjsFromApiUtcInTz,
  dayjsFromDayAndTimeStrings,
  dayjsFromLocalDateString,
  dayjsNowInTz,
  formatDateFromDayjsInTzWithLocale,
  formatDateFromDayjsWithLocale,
  formatDateFromStringWithLocale,
  formatTimeIntervalFromStringInTzWithLocale,
  stringHHmmFromStringInTz,
} from '@utils/dateWithTz.tsx';
import InputCompanyRamp from '@components/ringil3/Input/InputCompanyRamp/InputCompanyRamp.tsx';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix.tsx';
import InputHourManual from '@components/ringil3/Input/InputHour/InputHourManual.tsx';
import NoAvailableSlotsMessage from '@components/RingilComponents/NoAvailableSlots/NoAvailableSlotsMessage.tsx';
import { JobReferrer } from '@components/ringil3/Features/Job/jobReferrer.ts';
import {
  applySlotToTimeslot,
  buildDuplicationRequest,
  DistributionDefaults,
  Form,
  getOriginalDistribution,
  initDistribution,
  resolveNewTimeslots,
} from '@components/ringil3/Features/Expedition/DuplicateExpeditionSidebar/duplicateExpeditionMapping.ts';

enableMapSet();

export interface ExpeditionDuplicationOptions {
  handlingUnitCount?: number;
  ladenFactorEligible?: boolean;
  originalLadenFactor?: number;
  originalAllowPartialDelivery?: boolean;
}

interface ComponentProps {
  expeditionId: string;
  onSuccess?: () => void;
  expeditionDuplicationOptions?: ExpeditionDuplicationOptions;
}

/**
 * States in which the duplicated date cannot keep its reservation and the user has to pick a new
 * slot: the ramp is occupied (FULL), or the location was replaced by another one and the original
 * ramp does not exist there any more (LOCATION_SUPERSEDED).
 *
 * Module scope on purpose: it is read while the component body runs (hasConflictingTimeslots), so a
 * `const` inside the component would be in its temporal dead zone at that point.
 */
const needsNewTimeslot = (state?: ReservationStateEnum) =>
  state === ReservationStateEnum.FULL || state === ReservationStateEnum.LOCATION_SUPERSEDED;

/**
 * Seed duration used when the reservation's own length cannot be read — a superseded stop carries no
 * reservationFullDetail at all, and an occupied one can come back with an unusable time detail. The
 * user adjusts it in the editor.
 */
const FALLBACK_DURATION_MINUTES = 60;

export const useCreateDuplicateExpeditionSidebar = () => {
  const [visible, setVisible] = useState(false);

  const DuplicateExpeditionSidebar: React.FC<ComponentProps> = ({
                                                                  expeditionId,
                                                                  onSuccess,
                                                                  expeditionDuplicationOptions,
                                                                }) => {
    const {
      handlingUnitCount,
      ladenFactorEligible,
      originalLadenFactor,
      originalAllowPartialDelivery,
    } = expeditionDuplicationOptions;
    const {
      checkDuplicationExpedition,
      createDuplicationJob,
      saveDuplicationExpedition,
      getDistributionSettings,
      getExpeditionV4Detail,
      getFreeSlotsForLocationReservation,
      getCreateEditReservationSettings,
      listDocumentsForEntity,
    } = useContext(ApiContext);
    const { tr } = useTranslationLgs();
    const { showToastMessage } = useContext(ToastContext);
    const { version: asyncJobVersion } = useBetaVersion(BetaSurface.EXPEDITION_MULTI_DUPLICATION);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [duplicationCheck, setDuplicationCheck] = useState<CheckDuplicationExpeditionResponseOutDTO>();
    const [loading, setLoading] = useState(false);
    const [openExpeditionSections, setOpenExpeditionSections] = useState<Set<string>>(new Set());
    const [openReservationSections, setOpenReservationSections] = useState<Set<string>>(new Set());
    const [validReservation, setValidReservation] = useState<Map<string, boolean>>(new Map());
    const [slots, setSlots] = useState<Map<string, FreeSlotsWrapperOutDTO[]>>(new Map());
    const [settingsMap, setSettingsMap] = useState<Map<string, any>>(new Map());
    const [storing, setStoring] = useState(false);
    const [customNameSwitch, setCustomNameSwitch] = useState(false);
    const nav = useNavigation();
    const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
    const [distributionHours, setDistributionHours] = useState<number>(8);
    const [defaultResponseDeadlineMode, setDefaultResponseDeadlineMode] = useState<'relative' | 'absolute'>('relative');
    const [attachments, setAttachments] = useState<AttachmentUploadedOutDTO[]>([]);
    const [originalExpedition, setOriginalExpedition] = useState<ExpeditionDetailV4OutDTO>(null);
    const durationRefreshTimer = useRef<ReturnType<typeof setTimeout>>(null);

    const currencyLockEnabled = useSecuredFlag('duplicate-currency-lock', {
      kind: 'feature',
      description: 'Currency lock in duplicate sidebar',
      features: [FeaturesEnum.CURRENCY_LOCK],
    }).granted;
    const showDistributionFlag = useSecuredFlag('duplicate-distribution', {
      kind: 'visibility',
      description: 'Distribution selector in duplicate sidebar',
      permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
    });

    const openingHourIcons = {
      [OpeningHourStateEnum.NO_DATA]: <SvgQuestionMark />,
      [OpeningHourStateEnum.OK]: <SvgOK className="text-complementary-success" />,
      [OpeningHourStateEnum.COLLISION]: <SvgWarning className="text-complementary-warning" />,
      [OpeningHourStateEnum.CLOSED_EXCEPTION]: <SvgAlert className="text-complementary-error" />,
      [OpeningHourStateEnum.OPEN_EXCEPTION]: <SvgOK className="text-complementary-success" />,
    };

    useEffect(() => {
      listDocumentsForEntity(EntityTypeEnum.EXPEDITION, expeditionId, d => {
        setAttachments(d);
      })
    }, []);

    // Beta surface EXPEDITION_MULTI_DUPLICATION: NEW = async job + redirect to the job detail page,
    // ORIGINAL = the legacy synchronous save with results rendered inside this sidebar.
    const save = (data: Form) => {
      const body = buildDuplicationRequest(data, loggedUser);
      const callbacks = {
        onValidationFailed: (errors: any) => {
          setBeValidationErrors(errors);
          setStoring(false);
          setLoading(false);
        },
        onError: (error: ApiError, showStandardError: () => void) => {
          showStandardError();
          setStoring(false);
          setLoading(false);
        },
      };
      setStoring(true);
      if (asyncJobVersion === DefaultVersionEnum.NEW) {
        createDuplicationJob(
          body,
          d => {
            setStoring(false);
            setVisible(false);
            showToastMessage(
              tr('DuplicateExpeditionSidebar.jobCreated', 'Duplication job created'),
              tr('DuplicateExpeditionSidebar.jobCreatedDetail', 'Creating {{count}} duplications in the background.', { count: d.totalCount }),
              'success',
            );
            onSuccess?.();
            nav.navigate(nav.urlFunctions.createJobDetail(d.jobId, JobReferrer.EXPEDITIONS));
          },
          null,
          { ...callbacks, successToast: false },
        );
      } else {
        saveDuplicationExpedition(
          body,
          d => {
            setDuplicationCheck(d);
            setStoring(false);
          },
          null,
          callbacks,
        );
      }
    };

    const distributionDefaults: DistributionDefaults = { defaultResponseDeadlineMode, distributionHours, loggedUser, currencyLockEnabled };

    const contractedMuCodesRef = useRef<string[]>([]);
    const validationRules = yup.object().shape({
      distribution: yup.object().shape({
        distributionType: yup.string(),
        price: yup.object().when('distributionType', {
          is: (type: string) => !!type && type !== DistributionType.CONTRACTED, // pokud je jakýkoliv typ distribuce (CONTRACTED má cenu ze smlouvy)
          then: yup.object().shape({
            price: yup.number()
              .typeError(tr('CreateExpedition.priceShouldBeNumber', 'Price should be number'))
              .transform((value) => (isNaN(value) ? undefined : value))
              .nullable()
              .required(tr('CreateExpedition.mandatoryPrice', 'Price is required')),
            currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
          }).required(tr('CreateExpedition.mandatoryPrice', 'Price is required')),
          otherwise: yup.object().nullable(),
        }),
        selectedItems: yup.array().when('distributionType', {
          is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
          then: yup.array()
            .min(1, tr('CreateExpedition.mandatoryCarriers', 'Please select at least one carrier'))
            .required(tr('CreateExpedition.mandatoryCarriers', 'Please select at least one carrier')),
          otherwise: yup.array().nullable(),
        }),
        // same pricing rules as the create form (table, MU, level on the deadline day, carrier cells); the
        // original's MUs are read through a ref because the schema is built before the original is loaded
        contractedCarriers: contractedCarriersValidation(tr, () => contractedMuCodesRef.current, loggedUser.defaultTimezoneId ?? 'Europe/Prague'),
        responseDeadlineOffsetValue: yup.number().when(['distributionType', 'responseDeadlineMode'], {
          is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT' || type === 'CONTRACTED') && mode !== 'absolute',
          then: yup.number()
            .typeError(tr('InputDeadline.required', 'Offer deadline is required'))
            .min(1, tr('InputDeadline.required', 'Offer deadline is required'))
            .required(tr('InputDeadline.required', 'Offer deadline is required')),
          otherwise: yup.number().nullable(),
        }),
        responseDeadlineLocalTime: yup.mixed().when(['distributionType', 'responseDeadlineMode'], {
          is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT' || type === 'CONTRACTED') && mode === 'absolute',
          then: yup.mixed().required(tr('InputDeadline.required', 'Offer deadline is required')),
          otherwise: yup.mixed().nullable(),
        }),
      }).nullable(),
    });

    const form = useForm<Form>(
      validationRules,
      {
        duplicateReservations: false,
        copyAttachments: false,
        fromExpeditionId: expeditionId,
        distribution: initDistribution(distributionDefaults),
        copyToLocalDates: [],
      },
      d => save(d),
      false,
      false,
    );

    const [debouncedFormValues, isDebounced] = useDebounceWithEvents(form.values, 1000);

    useEffect(() => {
      check();
    }, [debouncedFormValues]);

    useEffect(() => {
      setLoading(true);
      checkDuplicationExpedition(
        {
          fromExpeditionId: expeditionId,
          duplicateReservations: false,
          copyAttachments: false,
          copyToLocalDates: [],
        },
        d => {
          setLoading(false);
          setDuplicationCheck(d);
        },
      );
      getDistributionSettings(d => {
        const hours = d.defaultHoursInFuture;
        setDistributionHours(hours);
        const mode: 'relative' | 'absolute' =
          d.responseDeadlineMode === ResponseDeadlineModeEnum.ABSOLUTE ? 'absolute' : 'relative';
        setDefaultResponseDeadlineMode(mode);
        form.setFieldValue('distribution.responseDeadlineMode', mode);
        form.setFieldValue('distribution.responseDeadlineOffsetValue', hours);
        if (mode === 'absolute') {
          form.setFieldValue('distribution.responseDeadlineLocalTime', dayjsNowInTz(loggedUser.defaultTimezoneId).add(hours, 'hour'));
        }
      });

      getExpeditionV4Detail(
        expeditionId,
        d => {
          setOriginalExpedition(d);
        },
      );
    }, []);

    const check = () => {
      if (isNullOrUndefined(form.values)) return;

      setLoading(true);
      checkDuplicationExpedition(
        {
          fromExpeditionId: expeditionId,
          duplicateReservations: form.values.duplicateReservations,
          copyAttachments: form.values.copyAttachments,
          copyToLocalDates: form.values.copyToLocalDates,
          newTimeslots: resolveNewTimeslots(form.values.newTimeSlots),
        },
        d => {
          setLoading(false);
          setDuplicationCheck(d);
          updateValidReservation(d.locations)

          const fullLocations = d.locations
            ?.flatMap(loc => loc.duplications
              .filter(dupl => needsNewTimeslot(dupl.reservationState))
              .map(() => loc.companyLocationOwnerId)
            )
            .filter((v, i, a) => v && a.indexOf(v) === i);

          if (fullLocations?.length) {
            const newLocations = fullLocations.filter(ownerId => !settingsMap.has(ownerId));
            console.log(newLocations);
            newLocations.forEach(ownerId => {
              getCreateEditReservationSettings(ownerId, s => {
                setSettingsMap(prev => new Map(prev).set(ownerId, s));
              });
            });
          }
        },
        null,
        {
          onValidationFailed: errors => {
            setBeValidationErrors(errors);
            setLoading(false);
          },
          onError: (error: ApiError, showStandardError: () => void) => {
            showStandardError();
            setLoading(false);
          },
        },
      );
    };

    const updateValidReservation = (locations: DuplicationLocationOutDTO[]) => {
      const newMap = new Map<string, boolean>();
      locations?.forEach((loc, locationIndex) => {
        loc.duplications?.forEach((dupl, duplIndex) => {
          const key = reservationDuplicationKey(locationIndex, duplIndex, dupl.id);
          if (dupl.reservationState === ReservationStateEnum.FREE || dupl.reservationState === ReservationStateEnum.FREE_NOSLOT) {
            newMap.set(key, true);
          } else if (needsNewTimeslot(dupl.reservationState)) {
            newMap.set(key, false);
          }
        });
      });
      setValidReservation(newMap);
    };

    const deleteDate = (id: string, key: string) => {
      form.useProduce(d => {
        d.copyToLocalDates = d.copyToLocalDates.filter(v => v !== id);
      });
      const newMap = new Map(form.values.newTimeSlots ?? []);
      newMap.delete(key);
      form.setFieldValue(form.names.newTimeSlots, newMap);
      closeOpenReservationSection(key);
    };


    const resolveText = (duplicationCheck: CheckDuplicationExpeditionResponseOutDTO) => {
      if (duplicationCheck?.firstDay?.openingMode === OpeningModeEnum.OPEN) return `${formatDateFromStringWithLocale(duplicationCheck?.firstDay?.localDate)} | non-stop`;
      if (!duplicationCheck?.firstDay?.intervals) return formatDateFromStringWithLocale(duplicationCheck?.firstDay?.localDate);
      return `${formatDateFromStringWithLocale(duplicationCheck?.firstDay?.localDate)} | ${duplicationCheck.firstDay.intervals.sinceHoursString} - ${
        duplicationCheck.firstDay.intervals.tillHoursString
      }`;
    };

    const toggleOpenExpeditionSection = (key: string) => {
      const newSet = new Set(openExpeditionSections);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      setOpenExpeditionSections(new Set(newSet));
    };

    const toggleOpenReservationSection = (key: string) => {
      const newSet = new Set(openReservationSections);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      setOpenReservationSections(new Set(newSet));
    };

    const closeOpenReservationSection = (key: string) => {
      const newSet = new Set(openReservationSections);
      if (newSet.has(key)) {
        newSet.delete(key);
        setOpenReservationSections(new Set(newSet));
      }
    };

    const hasColision = duplicationCheck?.locations
      ?.map(t => t.duplications)
      ?.flat()
      ?.some(t => t.openingHourState === OpeningHourStateEnum.COLLISION);

    const hasConflictingTimeslots: 'NA' | 'TRUE' | 'FALSE' =
      form.values.duplicateReservations && duplicationCheck?.locations?.length > 0
        ? duplicationCheck?.locations
          ?.map(t => t.duplications)
          ?.flat()
          ?.some(t => needsNewTimeslot(t.reservationState))
          ? 'TRUE'
          : 'FALSE'
        : 'NA';

    // Handling units of the original per manipulation unit — what the contracted prices are applied to.
    const contractedUnitsByMu = (originalExpedition?.locations ?? [])
      .flatMap(loc => loc.loadedOrders ?? [])
      .flatMap(order => order.items ?? [])
      .reduce<Record<string, number>>((units, item) => {
        const code = item.cargoTemplate?.code;
        if (code) units[code] = (units[code] ?? 0) + (item.amount ?? 0);
        return units;
      }, {});
    contractedMuCodesRef.current = muCodesOf(contractedUnitsByMu);

    const offerAvailableSlots = (key: string, localDate: string, durationInMinutes: number, companyLocationId: string, locationCustomerId: string) => {
      getFreeSlotsForLocationReservation(
        localDate,
        durationInMinutes,
        companyLocationId,
        [],
        {
          customerId: locationCustomerId,
          rampId: null,
          locationId: companyLocationId,
          labelIds: [],
          openGate: false,
          createExpedition: false,
          sendNotification: true,
        },
        data => {
          setSlots(prev => {
            const newMap = new Map(prev);
            newMap.set(key, data);
            return newMap;
          });
        });

    };

    const applySlot = (key: string, slot: TimeslotCalendarDTO, rampId: string) => {
      const current = form.values.newTimeSlots?.get(key);
      const updated = applySlotToTimeslot(current, slot, rampId);
      const newMap = new Map(form.values.newTimeSlots ?? []);
      newMap.set(key, updated);
      form.setFieldValue(form.names.newTimeSlots, newMap);
    };

    const reservationDuplicationKey = (locationIndex: number, duplicationIndex: number, day: string) => {
      return `${locationIndex}-${duplicationIndex}-${day}`;
    };

    const canEditDuration = (locationOwnerId: string) => {
      const isOwner = loggedUser.companyId === locationOwnerId;
      const settings = settingsMap.get(locationOwnerId);
      return isOwner ? settings?.editDuration === true : settings?.editDurationCounterParty === true;
    };


    return (
      <>
        <RingilSidebar
          visible={visible}
          heading={tr('DuplicateExpeditionSidebar.duplicateTransport', 'Duplication of transport')}
          onBack={() => {
            setVisible(false);
            if (duplicationCheck?.isFinishedState === true) {
              onSuccess?.();
            }
          }}
          onContinue={() => {
            setOpenReservationSections(new Set());
            form.validateAndSend();
          }}
          loading={storing}
          // The save button stays put and goes disabled rather than disappearing, so the footer does
          // not reflow while the duplication check resolves.
          disableSaveButton={!(duplicationCheck?.isFinishedState == false && duplicationCheck?.locations?.length > 0)}
          dataTestIdBack='expedition-duplicateMore-close'
          dataTestIdContinue='expedition-duplicateMore-save'
        >
          <div className={'flex flex-col gap-6'}>
            <BackendValidationComponent
              validationMessageFilter={error => !error.path?.startsWith('distribution')}
            />
            {duplicationCheck?.isFinishedState === true && (
              <div className={'flex flex-col gap-2'}>
                {duplicationCheck.isFinishedState === true && (
                  <COInlineMessage variant={'success'} icon={<SvgOK className="text-content-tertiary" />}>
                    {tr('DuplicateExpeditionSidebar.transportCreated', 'The transport has been created successfully.')}
                  </COInlineMessage>
                )}

                {duplicationCheck?.isSomeReservationFailed === true && (
                  <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
                    {tr('DuplicateExpeditionSidebar.someReservationsFailed', 'Some reservations could not be created.')}
                  </COInlineMessage>
                )}
              </div>
            )}

            <div className={'flex flex-col gap-2'}>
              <VerticalFieldAndValueWithIcon
                label={tr('DuplicateExpeditionSidebar.outgoingTransport', 'Outgoing transport')}
                valueText={duplicationCheck?.originalApplicationId}
                icon={<SvgForExpedition />}
              />
              <VerticalFieldAndValueWithIcon
                label={tr('DuplicateExpeditionSidebar.beginner', 'Beginner')}
                valueText={resolveText(duplicationCheck)}
                icon={<SvgCalendar />}
              />
            </div>
            <HorizontalLine variant={'tertiary'} />
            <div className={'flex flex-col gap-2'}>
              <RingilInputSwitch
                label={tr('DuplicateExpeditionSidebar.duplicateIncludingReservationDates', 'Duplicate including reservation dates')}
                value={form.values.duplicateReservations}
                onChange={v => {
                  form.useProduce(d => {
                    d.duplicateReservations = v;
                    if (!v) {
                      d.copyCarPlates = false;
                    }
                  });
                }}
                disabled={duplicationCheck?.isReservationsPresent !== true}
                dataTestId="expedition-duplicateMore-includeReservationDates"
              />

              <RingilInputSwitch
                label={tr('DuplicateExpeditionSidebar.duplicateIncludingCarPlateCopy', 'Duplicate including car plate')}
                value={form.values.copyCarPlates}
                onChange={v => {
                  form.useProduce(d => {
                    d.copyCarPlates = v;
                  });
                }}
                disabled={!form.values.duplicateReservations}
                className="pl-4"
                dataTestId="expedition-duplicateMore-includeCarPlate"
              />

              <RingilInputSwitch
                label={tr('DuplicateExpeditionSidebar.copyAttachmentsFromShipment(FV)', 'Copy attachments from shipment (FV)')}
                value={form.values.copyAttachments}
                onChange={v => {
                  form.useProduce(d => {
                    d.copyAttachments = v;
                  });
                }}
                disabled={duplicationCheck?.isAttachmentsPresent !== true}
                dataTestId="expedition-duplicateMore-copyAttachments"
              />
            </div>
            <HorizontalLine variant={'tertiary'} />

            {duplicationCheck?.locations?.length == 0 ? (
              <div className={'flex flex-col gap-2'}>
                <COHeadline variant={'h4'} icon={<SvgRepeatIcon />}
                            title={tr('DuplicateExpeditionSidebar.createNew', 'Create new')}
                            loading={loading} />
                <DuplicateDatesButtons
                  startLocalDate={duplicationCheck.firstDay.localDate}
                  computedLocalDates={form.values.copyToLocalDates}
                  onChanged={v =>
                    form.useProduce(d => {
                      d.copyToLocalDates = v;
                    })
                  }
                  dataTestId='expedition-duplicateMore-shiftDateBy'
                />

                <COInlineMessage variant={'info'} icon={<SvgAlert className="text-content-tertiary" />}>
                  {tr('DuplicateExpeditionSidebar.coInlineMessageShiftedDatesInAllTransports', 'All dates at other locations will be shifted by the same time interval as set in the original transport.')}
                </COInlineMessage>
              </div>
            ) : (
              <>
                {duplicationCheck?.locations.map((loc, locationIndex) => {
                  return (
                    <React.Fragment key={locationIndex}>
                      <div
                        className={` m-[-16px] px-4 py-6 gap-6 flex flex-col ${locationIndex % 2 == 1 ? 'bg-base-quaternary' : ''}`}>
                        <div className={'flex flex-row gap-6 items-center '}>
                          <div className={'text-content-brand font-medium min-w[100px]'}>{locationIndex + 1}.</div>
                          <div className={'text-content-secondary'}>
                            <div>{`${loc.postalCode} ${loc.city}`}</div>
                            <div data-test-id={`expedition-duplicateMore-location-${locationIndex}-locationName`}>{loc.locationName}</div>
                            {/* Both names are sent only when the stop's location resolved to a replacement. */}
                            {isNotNullOrUndefined(loc.supersededByLocationName) && (
                              <div className={'text-complementary-warning'}
                                   data-testid={`expedition-duplicateMore-location-${locationIndex}-supersededMarker`}>
                                {tr('DuplicateExpeditionSidebar.locationSupersededMarker', 'was {{oldName}}, now {{newName}}', {
                                  oldName: loc.supersededFromLocationName,
                                  newName: loc.supersededByLocationName,
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        {locationIndex > 0 && (
                          <COInlineMessage variant={'info'} icon={<SvgAlert className="text-content-tertiary" />}>
                            <COText
                              label={tr('DuplicateExpeditionSidebar.datesDeterminedAtTheBeginning', 'The dates are determined at the beginning of the route.')} />
                          </COInlineMessage>
                        )}

                        <div className={'grid grid-cols-[1.5fr_1.5fr_0.5fr] gap-x-2 text-black'}>
                          <div
                            className={'text-xs'}>{tr('DuplicateExpeditionSidebar.openingHours', 'Opening hours')}</div>
                          <div className={'text-xs'}>{tr('DuplicateExpeditionSidebar.reservation', 'Reservation')}</div>
                          <div></div>
                        </div>
                        {loc.duplications.map((dupl, duplIndex) => {
                          return (
                            <React.Fragment key={duplIndex}>
                              <div
                                key={duplIndex}
                                className={'grid grid-cols-[1.5fr_1.5fr_0.5fr] gap-x-2 text-black items-center'}
                              >
                                <div className="flex flex-row gap-2 items-center">
                                  {dupl.openingHourState && (
                                    <div className="flex flex-row justify-between items-center w-full">
                                      <div className="flex flex-row gap-2 items-center" data-testid={`expedition-duplicateMore-location-${locationIndex}-openingHourState`}>
                                        <span className="w-5 h-5 flex items-center justify-center">
                                          {openingHourIcons[dupl.openingHourState]}
                                        </span>
                                        <COText
                                          label={tr(`DuplicateExpeditionSidebar.openingHour${dupl.openingHourState}`, dupl.openingHourState)}
                                        />
                                      </div>
                                      <div className={'mr-4'} data-testid={`expedition-duplicateMore-location-${locationIndex}-openingHoursToggle`}>
                                        <SvgChevron
                                          className={clsx('mx-auto text-content-brand cursor-pointer transition-all duration-200 hover:text-complementary-brandDark',
                                            { 'rotate-180': openExpeditionSections.has(`${locationIndex}-${dupl.id}`) },
                                          )}
                                          onClick={() => toggleOpenExpeditionSection(`${locationIndex}-${dupl.id}`)}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {isNotNullOrUndefined(dupl.expeditionLink) && (
                                    <>
                                      <Link
                                        to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(dupl.expeditionLink.id))}
                                        onClick={() => setVisible(false)}
                                        data-testid={`expedition-duplicateMore-location-${locationIndex}-expeditionLink`}
                                      >
                                        {dupl.expeditionLink.applicationId}
                                      </Link>
                                    </>
                                  )}
                                </div>

                                <div className={'flex flex-row gap-2'} data-testid={`expedition-duplicateMore-location-${locationIndex}-reservationState`}>
                                  {dupl.reservationState === ReservationStateEnum.FREE && (
                                    <>
                                      <SvgOK className="text-complementary-success" />
                                      <COText label={tr('DuplicateExpeditionSidebar.empty', 'Empty')} />
                                    </>
                                  )}
                                  {dupl.reservationState === ReservationStateEnum.FREE_NOSLOT && (
                                    <>
                                      <div className="relative group cursor-pointer">
                                        <SvgWarning className="text-complementary-warning" />
                                        <div
                                          className="w-[100px] absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded px-2 py-1">
                                          <COText
                                            label={tr('DuplicateExpeditionSidebar.reservationWithoutTimeSlot', 'The reservation does not have a time slot')} />
                                        </div>
                                      </div>
                                      <COText label={tr('DuplicateExpeditionSidebar.empty', 'Empty')} />
                                    </>
                                  )}
                                  {dupl.reservationState === ReservationStateEnum.FULL && (
                                    <div className="flex flex-row justify-between items-center w-full">
                                      <div className="flex flex-row gap-2 items-center">
                                        <SvgAlert className="text-complementary-error" />
                                        <COText label={tr('DuplicateExpeditionSidebar.occupied', 'Occupied')} />
                                      </div>
                                      <SvgChevron
                                        className={clsx('mx-auto text-content-brand cursor-pointer transition-all duration-200 hover:text-complementary-brandDark',
                                          { 'rotate-180': openReservationSections.has(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) },
                                        )}
                                        onClick={() => toggleOpenReservationSection(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))}
                                      />
                                    </div>
                                  )}
                                  {dupl.reservationState === ReservationStateEnum.LOCATION_SUPERSEDED && (
                                    <div className="flex flex-row justify-between items-center w-full">
                                      <div className="flex flex-row gap-2 items-center">
                                        <SvgWarning className="text-complementary-warning" />
                                        <COText label={tr('DuplicateExpeditionSidebar.locationSuperseded', 'Location replaced')} />
                                      </div>
                                      <SvgChevron
                                        className={clsx('mx-auto text-content-brand cursor-pointer transition-all duration-200 hover:text-complementary-brandDark',
                                          { 'rotate-180': openReservationSections.has(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) },
                                        )}
                                        onClick={() => toggleOpenReservationSection(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))}
                                      />
                                    </div>
                                  )}
                                  {isNotNullOrUndefined(dupl.reservationLink) && (
                                    <>
                                      {dupl.reservationLink.isCreated === true && (
                                        <Link
                                          to={nav.createNavigationLink(nav.urlFunctions.createReservationDetail(dupl.reservationLink.id))} data-testid={`expedition-duplicateMore-location-${locationIndex}-reservationLink`}>
                                          {dupl.reservationLink.applicationId}
                                        </Link>
                                      )}
                                      {dupl.reservationLink.isCreated === false && (
                                        <>
                                          <SvgAlert className="text-complementary-error" />
                                          <COText label={tr('DuplicateExpeditionSidebar.notCreated', 'Not created')} />
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                                <div className={'flex flex-row justify-end items-center'}>
                                  <RingilButton
                                    mode={'table'}
                                    icon={<SvgDelete />}
                                    onClick={() => deleteDate(dupl.id, reservationDuplicationKey(locationIndex, duplIndex, dupl.id))}
                                  />
                                </div>
                                <div
                                  className="flex flex-row gap-2 text-content-secondary font-dm-sans text-[13px] font-normal">
                                  {dupl.openingHours?.dayWithInterval && dupl.openingHours.dayWithInterval.map((dayWithInterval) =>
                                    formatDateFromDayjsWithLocale(dayjsFromLocalDateString(dayWithInterval.localDate))).join(', ')}
                                </div>
                                <div
                                  className="flex flex-row gap-2 text-content-secondary font-dm-sans text-[13px] font-normal">
                                  {dupl.reservationFullDetail && formatDateFromDayjsInTzWithLocale(dayjsFromApiUtc(dupl.reservationFullDetail.utcStart), dupl.reservationFullDetail.timezoneId)}
                                </div>
                              </div>
                              {
                                openExpeditionSections.has(`${locationIndex}-${dupl.id}`) && (
                                <div className={'grid grid-cols-2 text-black gap-y-4'}>
                                  <div>
                                    <COText
                                      label={tr('DuplicateExpeditionSidebar.scheduledDate', 'Scheduled date')}
                                      className={'text-xs'} />
                                  </div>
                                  <div>
                                    <COText label={tr('DuplicateExpeditionSidebar.openingHours', 'Opening hours')}
                                            className={'text-xs'} />
                                  </div>
                                  <div>
                                    {dupl.deliverySlots?.dayWithInterval
                                      .reduce((previousValue, currentValue) => {
                                        const newValue = currentValue.intervals.map(i => {
                                          return {
                                            localDate: currentValue.localDate,
                                            hours: `${i.sinceHoursString} - ${i.tillHoursString}`,
                                            isCollision: currentValue.isCollision,
                                          };
                                        });

                                        const newValues =
                                          newValue.length > 0
                                            ? newValue
                                            : [
                                              {
                                                localDate: currentValue.localDate,
                                                hours: 'non-stop',
                                                isCollision: currentValue.isCollision,
                                              },
                                            ];

                                        return [...previousValue, ...newValues];
                                      }, [])
                                      .map((ds, dsIndex) => {
                                        return (
                                          <Fragment key={dsIndex}>
                                            <div className={'flex flex-row gap-2'}>
                                              {ds.isCollision ? <SvgWarning className="text-complementary-warning" /> :
                                                <SvgCalendar />}
                                              <div className={clsx({ 'text-complementary-draft': ds.isCollision })} data-testid={`expedition-duplicateMore-location-${locationIndex}-slot-${dsIndex}`}>
                                                {formatDateFromStringWithLocale(ds.localDate)} | {ds.hours}
                                              </div>
                                            </div>
                                          </Fragment>
                                        );
                                      })}
                                  </div>
                                  <div>
                                    {isNotNullOrUndefined(dupl.openingHours) &&
                                      remapOpeningHours(dupl.openingHours, tr).map((ds, dsIndex) => {
                                        return (
                                          <Fragment key={dsIndex}>
                                            <div className={'flex flex-row gap-2'}>
                                              <SvgCalendar />
                                              <div data-testid={`expedition-duplicateMore-location-${locationIndex}-openingHour-${dsIndex}`}>
                                                {formatDateFromStringWithLocale(ds.localDate)} | {ds.hours}
                                              </div>
                                            </div>
                                          </Fragment>
                                        );
                                      })}
                                    {isNullOrUndefined(dupl.openingHours) && (
                                      <div className={'flex flex-row gap-2 text-content-tertiary items-center'}>
                                        <SvgIconFile fill={'var(--ringil-tertiary-color)'} />
                                        <COText
                                          label={tr('DuplicateExpeditionSidebar.notSpecified', 'Not specified')} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {
                                openReservationSections.has(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) && (
                                <div>
                                  {/*
                                    Nothing is occupied here: the location was replaced, so the original ramp does not
                                    exist at it any more. reservationFullDetail is deliberately not used for the message
                                    (and is null anyway) — it describes the *old* location's ramp.
                                  */}
                                  {dupl.reservationState === ReservationStateEnum.LOCATION_SUPERSEDED && (
                                    <COInlineMessage variant={'warning'}
                                                     icon={<SvgWarning className="text-content-tertiary" />}>
                                      <div className="flex flex-row items-center justify-between">
                                        <div data-testid={`expedition-duplicateMore-location-${locationIndex}-supersededMessage`}>
                                          {tr(
                                            'DuplicateExpeditionSidebar.locationSupersededMessage',
                                            'This location was replaced by {{newLocationName}}. The original ramp no longer exists here — choose a new time slot.',
                                            { newLocationName: loc.supersededByLocationName ?? loc.locationName },
                                          )}
                                        </div>
                                        {(!form.values.newTimeSlots || !form.values.newTimeSlots.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))) && (
                                          <RingilButton
                                            mode={'table'}
                                            label={tr('DuplicateExpeditionSidebar.newTimeslot', 'Arrange new timeslot')}
                                            onClick={() => {
                                              const key = reservationDuplicationKey(locationIndex, duplIndex, dupl.id);
                                              const targetLocalDate = dupl.deliverySlots?.dayWithInterval?.[0]?.localDate ?? dupl.day;
                                              if (!form.values.newTimeSlots) form.setFieldValue(form.names.newTimeSlots, new Map());
                                              const newMap = new Map(form.values.newTimeSlots ?? []);
                                              newMap.set(key, {
                                                locationId: loc.locationId,
                                                sinceHourString: null,
                                                durationInMinutes: FALLBACK_DURATION_MINUTES,
                                                localDate: targetLocalDate,
                                                // The replacement location has its own ramps; the user picks one.
                                                rampId: null,
                                                id: dupl.id,
                                                timezoneId: dupl.timezoneId ?? null,
                                              });
                                              form.setFieldValue(form.names.newTimeSlots, newMap);
                                              offerAvailableSlots(key, targetLocalDate, FALLBACK_DURATION_MINUTES, loc.companyLocationId, loc.companyLocationOwnerId);
                                            }}
                                          />
                                        )}
                                      </div>
                                    </COInlineMessage>
                                  )}
                                  {dupl.reservationState === ReservationStateEnum.FULL && (
                                    <COInlineMessage variant={'warning'}
                                                     icon={<SvgAlert className="text-content-tertiary" />}>
                                      <div className="flex flex-row items-center justify-between">
                                        <div>
                                      <span>
                                      {tr('DuplicateExpeditionSidebar.timeslotFull', 'The timeslot date is occupied:')}
                                    </span>
                                          <span className="font-bold">
                                      {(() => {
                                        const detail = loc.duplications[duplIndex].reservationFullDetail;
                                        const hasNew = isNotNullOrUndefined(detail?.utcStart) && isNotNullOrUndefined(detail?.utcEnd) && isNotNullOrUndefined(detail?.timezoneId);
                                        const tz = hasNew ? detail.timezoneId : 'Europe/Prague';
                                        const since = hasNew ? detail.utcStart : detail?.since;
                                        const till = hasNew ? detail.utcEnd : detail?.till;
                                        return tr(
                                          'DuplicateExpeditionSidebar.timeslotFullRampNameAndTime',
                                          ' {{rampName}}, {{since}} - {{till}}',
                                          {
                                            rampName: detail?.rampName,
                                            since: stringHHmmFromStringInTz(since, tz),
                                            till: stringHHmmFromStringInTz(till, tz),
                                          },
                                        );
                                      })()}
                                    </span>
                                        </div>
                                        {(!form.values.newTimeSlots || !form.values.newTimeSlots.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))) && (
                                          <RingilButton
                                            mode={'table'}
                                            label={tr('DuplicateExpeditionSidebar.newTimeslot', 'Arrange new timeslot')}
                                            onClick={() => {
                                              const detail = dupl.reservationFullDetail;
                                              const hasNew = isNotNullOrUndefined(detail?.utcStart) && isNotNullOrUndefined(detail?.utcEnd) && isNotNullOrUndefined(detail?.timezoneId);
                                              const tz = hasNew ? detail.timezoneId : 'Europe/Prague';
                                              const sinceStr = hasNew ? detail.utcStart : detail?.since;
                                              const tillStr = hasNew ? detail.utcEnd : detail?.till;
                                              const sinceDayjs = dayjsFromApiUtcInTz(sinceStr, tz);
                                              const tillDayjs = dayjsFromApiUtcInTz(tillStr, tz);
                                              const rawDuration = tillDayjs.diff(sinceDayjs, 'minute');
                                              const durationInMinutes = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : FALLBACK_DURATION_MINUTES;
                                              // When UTC fields are present derive the local date from utcStart+tz,
                                              // not from the legacy localDate field which still holds the original expedition date.
                                              // A stop whose location was superseded carries no reservationFullDetail even when it
                                              // comes back FULL, so fall back to the duplication's own date rather than dereferencing it.
                                              const targetLocalDate = (hasNew ? sinceDayjs.format('YYYY-MM-DD') : detail?.localDate) ?? dupl.deliverySlots?.dayWithInterval?.[0]?.localDate ?? dupl.day;
                                              const targetDate = dupl.deliverySlots?.dayWithInterval?.find(d => d.localDate === targetLocalDate)?.localDate || targetLocalDate;
                                              if (!form.values.newTimeSlots) form.setFieldValue(form.names.newTimeSlots, new Map());
                                              const newMap = new Map(form.values.newTimeSlots ?? []);
                                              newMap.set(reservationDuplicationKey(locationIndex, duplIndex, dupl.id), {
                                                locationId: loc.locationId,
                                                sinceHourString: null,
                                                durationInMinutes: durationInMinutes,
                                                localDate: targetLocalDate,
                                                rampId: detail?.rampId,
                                                id: dupl.id,
                                                // utcStart / utcEnd intentionally absent — set by applySlot when user picks a slot
                                                timezoneId: hasNew ? detail.timezoneId : null,
                                              });
                                              form.setFieldValue(form.names.newTimeSlots, newMap);
                                              offerAvailableSlots(reservationDuplicationKey(locationIndex, duplIndex, dupl.id), targetDate, durationInMinutes, loc.companyLocationId, loc.companyLocationOwnerId);
                                            }}
                                          />
                                        )}
                                      </div>
                                    </COInlineMessage>
                                  )}
                                  {form.values.newTimeSlots && form.values.newTimeSlots.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) && (
                                    <div className="mt-2 p-4 bg-content-quaternary">
                                      <COHeadline variant={'h3'}
                                                  title={tr(`DuplicateExpeditionSidebar.changeTimeslot`, `Change timeslot`)}></COHeadline>

                                      {slots.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) && (
                                        <Panel
                                          headingText={`${tr('DuplicateExpeditionSidebar.availableSlots', 'Available slots')}: ${formatDateFromStringWithLocale(form.values.newTimeSlots?.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))?.localDate)}`}
                                        >
                                        <ColWithGap>
                                            {slots.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))[0].freeSlots.map((s, i) => {
                                              return (
                                                <VerticalFieldAndValue label={s.name} labelMode={'normal'} key={i}>
                                                  {s.slots.length == 0 && (
                                                    <NoAvailableSlotsMessage unavailability={s.unavailability} />
                                                  )}
                                                  <div className="flex flex-wrap gap-2">
                                                    {s.slots.map((slot, j) => {
                                                      return (
                                                        <ClickableCursor onClick={() => {
                                                          applySlot(reservationDuplicationKey(locationIndex, duplIndex, dupl.id), slot, s.rampId);
                                                        }}>
                                                          <COSelect
                                                            text={(() => {
                                                              const hasNew = isNotNullOrUndefined(slot.utcStart) && isNotNullOrUndefined(slot.utcEnd) && isNotNullOrUndefined(slot.timezoneId);
                                                              const tz = hasNew ? slot.timezoneId : 'Europe/Prague';
                                                              const since = hasNew ? slot.utcStart : slot.since;
                                                              const till = hasNew ? slot.utcEnd : slot.till;
                                                              return formatTimeIntervalFromStringInTzWithLocale(false, since, till, tz, tr);
                                                            })()}
                                                            key={j} />
                                                        </ClickableCursor>
                                                      );
                                                    })}
                                                  </div>
                                                </VerticalFieldAndValue>
                                              );
                                            })}
                                          </ColWithGap>
                                        </Panel>
                                      )}
                                      <div className="w-full grid grid-cols-2 gap-4">
                                        <VerticalFieldAndValue
                                          label={tr(`CreateTimeslotSidebar.ramp`, `Ramp`)}
                                          labelMode={'normal'}
                                          errorMessage={findValidationMessage(form.validationErrors, 'timeslot.rampId', tr)}
                                        >
                                          <InputCompanyRamp
                                            value={form.values.newTimeSlots?.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))?.rampId}
                                            onChange={v => {
                                              const current = form.values.newTimeSlots?.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id));
                                              const updated = { ...current, rampId: v };
                                              const newMap = new Map(form.values.newTimeSlots ?? []);
                                              newMap.set(reservationDuplicationKey(locationIndex, duplIndex, dupl.id), updated);
                                              form.setFieldValue(form.names.newTimeSlots, newMap);
                                            }}
                                            companyLocationId={loc.companyLocationId}
                                            companyId={loc.companyLocationOwnerId}
                                            showTemporaryClosed={false}
                                          />
                                        </VerticalFieldAndValue>
                                        <VerticalFieldAndValue
                                          label={tr(`CreateTimeslotSidebar.duration`, `Duration`)}
                                          labelMode={'normal'}
                                          errorMessage={findValidationMessage(form.validationErrors, 'timeslot.durationInMin', tr)}
                                        >
                                          <InputTextWithSuffix
                                            suffix={'min'}
                                            value={form.values.newTimeSlots?.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))?.durationInMinutes}
                                             onChange={v => {
                                              const key = reservationDuplicationKey(locationIndex, duplIndex, dupl.id);
                                              const current = form.values.newTimeSlots?.get(key);
                                              const parsedDuration = parseNumberOrUndefined(v);
                                              const utcEnd = current?.utcStart && current?.timezoneId && parsedDuration
                                                ? apiUtcFromDayjs(dayjsFromApiUtcInTz(current.utcStart, current.timezoneId).add(parsedDuration, 'minute'))
                                                : current?.utcEnd ?? null;
                                              const updated: DuplicationMoreReservationTimeslotsDTO = {
                                                ...current,
                                                durationInMinutes: parsedDuration,
                                                utcEnd,
                                              };
                                              const newMap = new Map(form.values.newTimeSlots ?? []);
                                              newMap.set(key, updated);
                                              form.setFieldValue(form.names.newTimeSlots, newMap);
                                              if (parsedDuration >= 10 && current?.localDate) {
                                                clearTimeout(durationRefreshTimer.current);
                                                durationRefreshTimer.current = setTimeout(() => {
                                                  offerAvailableSlots(key, current.localDate, parsedDuration, loc.companyLocationId, loc.companyLocationOwnerId);
                                                }, 800);
                                              }
                                            }}
                                            readOnly={!canEditDuration(loc.companyLocationOwnerId)}
                                          />
                                        </VerticalFieldAndValue>
                                      </div>
                                      <VerticalFieldAndValue
                                        label={tr(`CreateTimeslotSidebar.arrival`, `Arrival`)}
                                        labelMode={'normal'}
                                        errorMessage={findValidationMessage(form.validationErrors, 'timeslot.arrivalHourString', tr)}
                                      >
                                        <InputHourManual
                                          value={form.values.newTimeSlots?.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id))?.sinceHourString ?? ''}
                                          onChange={v => {
                                            const key = reservationDuplicationKey(locationIndex, duplIndex, dupl.id);
                                            const current = form.values.newTimeSlots?.get(key);
                                            const tz = current?.timezoneId;
                                            const utcStartDayjs = (tz && current?.localDate && v)
                                              ? dayjsFromDayAndTimeStrings(current.localDate, v, tz)
                                              : null;
                                            const utcStart = utcStartDayjs ? apiUtcFromDayjs(utcStartDayjs) : null;
                                            const utcEnd = utcStartDayjs && current?.durationInMinutes
                                              ? apiUtcFromDayjs(utcStartDayjs.add(current.durationInMinutes, 'minute'))
                                              : null;
                                            const updated: DuplicationMoreReservationTimeslotsDTO = {
                                              locationId: current.locationId,
                                              sinceHourString: v,
                                              durationInMinutes: current.durationInMinutes,
                                              localDate: current.localDate,
                                              rampId: current.rampId,
                                              id: current.id,
                                              timezoneId: current.timezoneId,
                                              utcStart,
                                              utcEnd,
                                            };
                                            const newMap = new Map(form.values.newTimeSlots ?? []);
                                            newMap.set(key, updated);
                                            form.setFieldValue(form.names.newTimeSlots, newMap);
                                          }}
                                        />
                                      </VerticalFieldAndValue>
                                      {validReservation.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) && (
                                        <COInlineMessage variant={'success'}>{tr(`DuplicateExpeditionSidebar.reservationPossible`, `Reservation is possible in this time and ramp`)}</COInlineMessage>
                                      )}
                                      {validReservation.get(reservationDuplicationKey(locationIndex, duplIndex, dupl.id)) == false && (
                                        <COInlineMessage variant={'error'}>
                                          {tr(`DuplicateExpeditionSidebar.reservationImpossible`, `Reservation is not possible in this time and ramp`)}
                                        </COInlineMessage>
                                      )}
                                      <div className="mt-2">
                                        <RingilButton
                                          mode={'filled-rectangle'}
                                          label={tr('DuplicateExpeditionSidebar.saveNewTimeslot', 'Save')}
                                          onClick={() => {
                                            check();
                                            toggleOpenReservationSection(reservationDuplicationKey(locationIndex, duplIndex, dupl.id));
                                          }}
                                        />
                                        <RingilButton
                                          mode={'text-only'}
                                          label={tr('DuplicateExpeditionSidebar.cancelNewTimeslot', 'Cancel')}
                                          onClick={() => {
                                            const newMap = new Map(form.values.newTimeSlots ?? []);
                                            newMap.delete(reservationDuplicationKey(locationIndex, duplIndex, dupl.id));
                                            form.setFieldValue(form.names.newTimeSlots, newMap);
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {locationIndex === 0 &&  (
                          <>
                            {loc.duplications.length <= 9 && (
                              <>
                                <COHeadline
                                  title={tr('DuplicateExpeditionSidebar.duplicateInFollowingDates', 'Duplicate in the following dates.')}
                                  variant={'h3'}
                                  icon={
                                    <SvgRepeatIcon
                                      className={clsx({
                                        'animate-spin': loading || isDebounced,
                                      })}
                                    />
                                  }
                                />

                                <DuplicateDatesButtons
                                  startLocalDate={duplicationCheck.firstDay.localDate}
                                  computedLocalDates={form.values.copyToLocalDates}
                                  onChanged={v =>
                                    form.useProduce(d => {
                                      d.copyToLocalDates = v;
                                    })
                                  }
                                />
                              </>
                            )}


                            {loc.duplications.length > 9 && (
                              <COInlineMessage
                                variant={"warning"}
                                icon={<SvgAlert className="text-content-tertiary" />}
                              >
                                {tr('DuplicateExpeditionSidebar.coInlineMessageTooManyDuplications', 'Duplication limit reached. No more duplication available')}
                              </COInlineMessage>
                            )}
                          </>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
            {duplicationCheck?.isFinishedState === false && (
              <div className={'flex flex-col gap-2'}>
                {hasColision === true && (
                  <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
                    {tr('DuplicateExpeditionSidebar.coInlineMessageOpeningHoursConflict', 'You have a conflict with the opening hours.')}
                  </COInlineMessage>
                )}
                {hasConflictingTimeslots === 'TRUE' && (
                  <COInlineMessage variant={'error'} icon={<SvgWarningExclamation />}>
                    {tr('DuplicateExpeditionSidebar.coInlineMessageOccupiedTimeslots', 'Some dates have occupied timeslots. The transport will be created, but it will not have a time slot reservation.')}
                  </COInlineMessage>
                )}
                {hasConflictingTimeslots === 'FALSE' && (
                  <COInlineMessage variant={'success'} icon={<SvgOK className="text-content-tertiary" />}>
                    {tr('DuplicateExpeditionSidebar.coInlineMessageTimeslotsAvailable', 'Timeslots for all specified dates are available.')}
                  </COInlineMessage>
                )}
              </div>
            )}

            <SecuredArea
              id="duplicate-distribution"
              label="Distribution selector" description="Distribution settings for the duplicated shipment: how carriers are asked for quotes"
              visible={[showDistributionFlag, { type: 'data', label: 'locations.length > 0', satisfied: duplicationCheck?.locations?.length > 0 }]}
            >
              <>
                <BackendValidationComponent
                  validationMessageFilter={error => error.path?.startsWith('distribution')}
                />
                <CarrierDistributionSelector
                  // the copies keep the source's Linka (tmplV2Id), so its type rule too
                  allowedDistributionTypes={distributionTypesForShipment(!!originalExpedition?.tmplV2Id, [
                    DistributionType.DO_NOT_ASSIGN,
                    DistributionType.SPOT,
                    DistributionType.FIXED,
                    DistributionType.ASSIGN,
                  ])}
                  contractedUnitsByMu={contractedUnitsByMu}
                  pickupTimezoneId={originalExpedition?.locations?.[0]?.timezoneId}
                  distribution={form.values.distribution}
                  originalDistribution={getOriginalDistribution(undefined, duplicationCheck, distributionDefaults, { originalLadenFactor, originalAllowPartialDelivery })}
                  onChange={v => {
                    form.setFieldValue(form.names.distribution, v);
                  }}
                  validationErrors={form.validationErrors
                    .filter(e => e.path?.startsWith('distribution'))
                    .map(e => ({ path: e.path || '', message: e.message }))}
                  aiCarrierAutoSelectionDto={{
                    loadingLocationId: duplicationCheck?.locations[0]?.locationId,
                    unloadingLocationId: duplicationCheck?.locations[duplicationCheck.locations.length - 1]?.locationId,
                    loadingCity: duplicationCheck?.locations[0]?.city,
                    unloadingCity: duplicationCheck?.locations[duplicationCheck.locations.length - 1]?.city,
                  }}
                  ladenFactorEligible={ladenFactorEligible}
                  handlingUnitCount={handlingUnitCount}
                  expeditionValidations={[
                    {
                      locations: originalExpedition?.locations
                        .map(loc => ({
                          id: loc.id,
                          companyLocationId: loc.companyLocationId,
                          city: loc.city,
                          street: loc.streetNr,
                          country: loc.country,
                          postalCode: loc.postalCode,
                          orders: loc.loadedOrders?.map(order => ({
                            items: order.items?.map((item) => ({
                              length: item.length,
                              width: item.width,
                              height: item.height,
                              weight: item.weight,
                              amount: item.amount,
                            })),
                            loadingLocationId: loc.id,
                            unloadingLocationId: originalExpedition?.locations.filter(loc => loc.unloadedOrders?.some(o => o.id === order.id))[0]?.id,
                            totalWeight: order.orderTotalWeight,
                          })),
                          contactEmail: loc.selectedPerson?.contactEmail,
                          contactName: loc.selectedPerson?.contactName,
                          contactPhone: loc.selectedPerson?.contactPhone,
                          deliverySlot: loc.deliverySlot?.dayWithInterval ? {
                            days: loc.deliverySlot.dayWithInterval,
                          } : null,
                        })),
                      cargoType: originalExpedition?.cargoTypeCode ? {
                        cargoTypeCode: originalExpedition.cargoTypeCode,
                      } : null,
                      incotermsValue: originalExpedition?.incoterms?.incotermsValue,
                      attachments: attachments.map(attachment => ({
                        documentType: attachment.documentType,
                        contentType: attachment.contentType,
                      })),
                    },
                  ]}
                  dataTestIdPrefix='carrierSelection'
                />
              </>
            </SecuredArea>

            <div className={'flex flex-col gap-2'}>
              <RingilInputSwitch
                label={tr('DuplicateExpeditionSidebar.setCustomNameSwitch', 'Set custom name')}
                value={customNameSwitch}
                onChange={v => {
                  setCustomNameSwitch(v);
                }}
              />

              {customNameSwitch && (
                <RingilTextArea
                  value={form.values.expeditionName}
                  onChange={value =>
                    form.setFieldValue('expeditionName', value)}
                  placeholder={tr('DuplicateExpeditionSidebar.enterExpeditionName', 'Enter expedition name')}
                  maxLength={255}
                  rows={2}
                />
              )}
            </div>

          </div>
        </RingilSidebar>
      </>
    );
  };

  return { DuplicateExpeditionSidebar, visible, setVisible };
};
